import { useState } from "react";
import { useNavigate } from "react-router";
import { Eye, EyeOff, Loader2, ShieldCheck, Zap, BarChart3, Lock } from "lucide-react";
import { account } from "../../lib/appwrite";
import logo from "../../assets/Solacesmartlogo.webp";

export function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("connect@solacesmart.com");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await account.createEmailPasswordSession(email.trim(), password);
            navigate("/admin");
        } catch (err: any) {
            setError(err?.message || "Invalid email or password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex font-sans">
            {/* ── Left panel — form ─────────────────────────────── */}
            <div className="flex-1 flex flex-col justify-center px-8 sm:px-14 lg:px-20 bg-white">
                <div className="max-w-sm w-full mx-auto">
                    <img src={logo} alt="Solace Smart Solutions" className="h-8 mb-8 object-contain" />

                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">Sign in</h1>
                    <p className="text-sm text-gray-500 mb-7">Admin dashboard — authorised access only.</p>

                    <form onSubmit={handleLogin} className="space-y-4">
                        {/* Email */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                                Email
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="username"
                                className="w-full px-3.5 py-2.5 rounded-md border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300 transition"
                                placeholder="admin@example.com"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPw ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    className="w-full px-3.5 py-2.5 pr-10 rounded-md border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300 transition"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Error */}
                        {error && (
                            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                                {error}
                            </p>
                        )}

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex items-center justify-center gap-2 bg-[#1a2d50] hover:bg-[#0f1c35] text-white text-sm font-semibold py-2.5 rounded-md transition-colors shadow-sm disabled:opacity-60 mt-2"
                        >
                            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign In"}
                        </button>
                    </form>

                    <p className="mt-8 text-xs text-gray-400 text-center">
                        Solace Smart Solutions &copy; {new Date().getFullYear()}
                    </p>
                </div>
            </div>

            {/* ── Right panel — branding ────────────────────────── */}
            <div className="hidden lg:flex w-[46%] bg-[#1a2d50] flex-col justify-center px-14 relative overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full border border-white/5" />
                <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full border border-white/5" />

                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3.5 py-1.5 text-xs text-white/70 font-medium w-fit mb-8">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#E9A319]" /> Secure Admin Access
                </div>

                <h2 className="text-3xl font-bold text-white leading-snug mb-3">
                    Manage your<br />platform with ease
                </h2>
                <p className="text-sm text-white/60 mb-8 leading-relaxed">
                    Full control over enquiries, blog posts, products and services — all in one place.
                </p>

                <div className="space-y-3">
                    {[
                        { icon: BarChart3, text: "Live enquiry & content management" },
                        { icon: Zap, text: "Instant product & service updates" },
                        { icon: Lock, text: "Secured by Appwrite Authentication" },
                    ].map(({ icon: Icon, text }) => (
                        <div key={text} className="flex items-center gap-3 text-sm text-white/70">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#E9A319] shrink-0" />
                            <Icon className="w-4 h-4 text-white/40 shrink-0" />
                            {text}
                        </div>
                    ))}
                </div>

                {/* Faded logo */}
                <img src={logo} alt="" className="absolute bottom-8 right-8 h-10 object-contain opacity-10" />
            </div>
        </div>
    );
}
